/**
 * Column visibility drawer options and related helpers for the Offers grid.
 * Single source of truth for field labels used by the UI and Excel export.
 */

/** Base column configuration for visibility toggle (episode columns added via merge) */
export const BASE_COLUMN_OPTIONS = [
  { field: "createdAt", label: "Created", defaultHidden: true },
  { field: "id", label: "ID", defaultHidden: false },
  { field: "needsAudit", label: "Needs Audit", defaultHidden: false },
  { field: "tapOfferStatus", label: "TAP Offer Status", defaultHidden: false },
  { field: "offerTableRowRlId", label: "RL ID", defaultHidden: false },
  { field: "licensingJobId", label: "Licensing Job ID", defaultHidden: true },
  { field: "dealId", label: "Deal ID", defaultHidden: false },
  { field: "dealName", label: "Deal Name", defaultHidden: false },
  { field: "catalogId", label: "Catalog ID", defaultHidden: false },
  { field: "catalogName", label: "Catalog Name", defaultHidden: false },
  { field: "readyToPublish", label: "Ready to Publish", defaultHidden: false },
  { field: "offerStatus", label: "RL Offer Status", defaultHidden: false },
  { field: "dateTaken", label: "Date Taken", defaultHidden: false },
  { field: "media", label: "Media", defaultHidden: false },
  { field: "territory", label: "Territory", defaultHidden: false },
  { field: "languages", label: "Languages", defaultHidden: false },
  { field: "format", label: "Format", defaultHidden: false },
  { field: "termStart", label: "Term Start", defaultHidden: false },
  { field: "termEnd", label: "Term End", defaultHidden: false },
  {
    field: "startDateStatus",
    label: "Start Date Status",
    defaultHidden: false,
  },
  { field: "endDateStatus", label: "End Date Status", defaultHidden: false },
  { field: "rating", label: "Rating", defaultHidden: false },
  { field: "rateCardType", label: "Rate Card Type", defaultHidden: false },
  { field: "rateCardId", label: "Rate Card ID", defaultHidden: false },
  { field: "rateCardName", label: "Rate Card Name", defaultHidden: false },
  { field: "windows", label: "Window", defaultHidden: false },
  { field: "windowNum", label: "Window Number", defaultHidden: false },
  { field: "tier", label: "Tier", defaultHidden: false },
  { field: "licenseFee", label: "License Fee", defaultHidden: false },
  { field: "minDeemedPrice", label: "Min Deemed Price", defaultHidden: false },
  { field: "seasonPassAmt", label: "Season Pass Amt", defaultHidden: false },
  { field: "pmxPlatform", label: "PMX Platform", defaultHidden: false },
  { field: "currency", label: "Currency", defaultHidden: false },
  { field: "percentageSplit", label: "Percentage Split", defaultHidden: false },
  { field: "emaPriceType", label: "EMA Price Type", defaultHidden: false },
  { field: "viewingPeriod", label: "Viewing Period", defaultHidden: false },
  { field: "preSell", label: "Pre Sell", defaultHidden: false },
  { field: "subLanguage", label: "Sub Language", defaultHidden: false },
  { field: "dubLanguage", label: "Dub Language", defaultHidden: false },
  { field: "tprProgramName", label: "TPR Program Name", defaultHidden: false },
  { field: "ocad", label: "Consumer Announcement Date", defaultHidden: false },
  {
    field: "suppressionLiftDate",
    label: "Suppression Lift Date",
    defaultHidden: false,
  },
  { field: "exclusivity", label: "Exclusivity", defaultHidden: false },
  {
    field: "holdbackLanguages",
    label: "Holdback Languages",
    defaultHidden: false,
  },
  {
    field: "languageAvailsDisplay",
    label: "Language Avails Display",
    defaultHidden: false,
  },
  {
    field: "availsTypeOverride",
    label: "Avails Type Override",
    defaultHidden: false,
  },
  {
    field: "informationalRestrictions",
    label: "Informational Restrictions",
    defaultHidden: false,
  },
  { field: "message", label: "Message", defaultHidden: true },
];

/** Episode column options (inserted when feature flag is on) */
export const EPISODE_COLUMN_OPTIONS = [
  { field: "episodeTier", label: "Episode Tier", defaultHidden: false },
  {
    field: "episodeEmaPriceType",
    label: "Episode EMA Price Type",
    defaultHidden: false,
  },
];

/** Base filterable columns (episode filter added via merge in grid) */
export const BASE_FILTER_COLUMNS = [
  { field: "tapOfferStatus", label: "TAP Offer Status" },
  { field: "needsAudit", label: "Needs Audit" },
  { field: "readyToPublish", label: "Ready to Publish" },
  { field: "offerStatus", label: "RL Offer Status" },
  { field: "dealId", label: "Deal ID" },
  { field: "dealName", label: "Deal Name" },
  { field: "catalogId", label: "Catalog ID" },
  { field: "catalogName", label: "Catalog Name" },
  { field: "media", label: "Media" },
  { field: "territory", label: "Territory" },
  { field: "languages", label: "Languages" },
  { field: "format", label: "Format" },
  { field: "termStart", label: "Term Start" },
  { field: "termEnd", label: "Term End" },
  { field: "tier", label: "Tier" },
  { field: "emaPriceType", label: "EMA Price Type" },
  { field: "viewingPeriod", label: "Viewing Period" },
  { field: "currency", label: "Currency" },
  { field: "pmxPlatform", label: "PMX Platform" },
  { field: "rateCardType", label: "Rate Card Type" },
  { field: "rateCardName", label: "Rate Card Name" },
  { field: "windows", label: "Window" },
  { field: "windowNum", label: "Window Number" },
  { field: "exclusivity", label: "Exclusivity" },
  { field: "preSell", label: "Pre Sell" },
];

export const EPISODE_FILTER_COLUMN = {
  field: "episodeTier",
  label: "Episode Tier",
};

/** Fields in COLUMN_OPTIONS that are not included in the Excel download */
const OFFERS_EXCEL_EXPORT_SKIP_FIELDS = new Set(["createdAt", "needsAudit"]);

/**
 * Merged column options for the grid (visibility drawer), matching prior useMemo behavior.
 * @param {boolean} hasEpisodeFeature
 */
export function getMergedColumnOptionsForGrid(hasEpisodeFeature) {
  if (hasEpisodeFeature) {
    const withEpisodeTier = [...BASE_COLUMN_OPTIONS];
    const seasonPassAmtIdx = withEpisodeTier.findIndex(
      (c) => c.field === "seasonPassAmt",
    );
    if (seasonPassAmtIdx > -1) {
      withEpisodeTier.splice(
        seasonPassAmtIdx + 1,
        0,
        EPISODE_COLUMN_OPTIONS[0],
      );
    }
    const emaPriceTypeIdx = withEpisodeTier.findIndex(
      (c) => c.field === "emaPriceType",
    );
    if (emaPriceTypeIdx > -1) {
      withEpisodeTier.splice(emaPriceTypeIdx + 1, 0, EPISODE_COLUMN_OPTIONS[1]);
    }
    return withEpisodeTier;
  }
  return BASE_COLUMN_OPTIONS;
}

/**
 * Ordered `{ field, header }` for Offers (and Offer History) Excel export.
 * Labels come from merged column options; episode columns follow feature flag.
 *
 * @param {boolean} hasEpisodeFeature - TAP_OFFER_EPISODE_TIER_AND_EPISODE_EMA_PRICE_TYPE
 * @returns {{ field: string, header: string }[]}
 */
export function getOfferTableExcelExportColumns(hasEpisodeFeature) {
  return getMergedColumnOptionsForGrid(hasEpisodeFeature)
    .filter((c) => !OFFERS_EXCEL_EXPORT_SKIP_FIELDS.has(c.field))
    .map((c) => ({ field: c.field, header: c.label }));
}
